import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, map, startWith, switchMap } from 'rxjs';
import { DataBagService } from 'src/app/common/data-bag.service';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { OrganizationDialogAddComponent } from 'src/app/organization/organization-dialog-add/organization-dialog-add.component';
import { OrganizationGusImportService } from 'src/app/organization/organization/organization-data-download/organization-gus-import.service';
import { OrganizationService } from 'src/app/organization/organization/organization.service';
import { FilteredOrganization, UserData } from 'src/app/reservation/reservation.model';
import { ReservationService } from 'src/app/reservation/reservation.service';
import { TourismEventsUserAddComponent } from '../tourism-events-user-add/tourism-events-user-add.component';

enum CustomerType {
	PRIVATE = 0,
	COMPANY = 1,
}

@Component({
	selector: 'app-tourism-events-organization-dialog',
	templateUrl: './tourism-events-organization-dialog.component.html',
	styleUrls: ['./tourism-events-organization-dialog.component.scss'],
})
export class TourismEventsOrganizationDialogComponent implements OnInit {
	constructor(
		private readonly api: DataBagService,
		public bag: DataBagService,
		public dialogRef: MatDialogRef<TourismEventsOrganizationDialogComponent>,
		public dialog: MatDialog,
		public service: OrganizationService,
		public loader: LoaderService,
		public _reservationService: ReservationService,
		private readonly gusImport: OrganizationGusImportService,
	) {}

	@Output() organizationChanged = new EventEmitter();
	@Output() clientChanged = new EventEmitter();

	protected organizationSelect = new FormControl(this._reservationService.organizations);
	protected usersSelect = new FormControl(this._reservationService.users);
	protected itemsBeforeDataLoad = 9;
	filteredOrganizations: FilteredOrganization[];
	filteredUsers: UserData[];
	customerType = CustomerType;
	selectedCustomerType = CustomerType.COMPANY;
	selectedUser: number = null;

	data = {
		ownerUser: null,
		ownerOrganization: null,
	};

	ngOnInit() {
		this._reservationService.loadUsers();
		this._reservationService.loadOrganizations();
		this._reservationService.loadNationalities();
		this.filteredUsers = this._reservationService.users;
		this.filteredOrganizations = this._reservationService.organizations;
		this.organizationSelect.valueChanges
			.pipe(
				startWith(''),
				debounceTime(400),
				map((value) => this._filterOrganizations(value || '')),
			)
			.subscribe((filtered) => {
				this.filteredOrganizations = filtered;
			});

		this.usersSelect.valueChanges
			.pipe(
				startWith(''),
				debounceTime(400),
				map((value) => this._filterUsers(value || '')),
			)
			.subscribe((filtered) => {
				this.filteredUsers = filtered;
			});
	}

	closeDialog() {
		this.dialogRef.close();
	}

	dataDownload() {
		this.loadAdditionalData();
		this.gusImport
			.importCompany()
			.pipe(switchMap(() => this.dialog.open(OrganizationDialogAddComponent).afterClosed()))
			.subscribe(() => {
				this._reservationService.loadOrganizations();
			});
	}

	addUserData() {
		this.dialog.open(TourismEventsUserAddComponent);
	}

	protected loadAdditionalData(): void {
		this.loader.show();
		this.bag.get('/organization/role/', (result, error) => {
			if (result) {
				this.service.enterpriseData.organizationRoles = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/organization/department/', (result, error) => {
			if (result) {
				this.service.enterpriseData.organizationDepartments = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/location/?limit=9999', (result, error) => {
			if (result) {
				this.service.enterpriseData.locations = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/sell_category/?limit=999', (result, error) => {
			if (result) {
				this.service.enterpriseData.sellCategory = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/account_kind/', (result, error) => {
			if (result) {
				this.service.enterpriseData.accountKind = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/address_kind/', (result, error) => {
			if (result) {
				this.service.enterpriseData.addressKind = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/currency/', (result, error) => {
			if (result) {
				this.service.enterpriseData.currency = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/organization/department_kind/', (result, error) => {
			if (result) {
				this.service.enterpriseData.departmentKinds = result.results;
				this.loaderDisplay();
			}
		});
		this.bag.get('/profile/all_users/', (result, error) => {
			if (result) {
				this.service.enterpriseData.departmentUsers = result.results;
				this.loaderDisplay();
			}
		});
	}

	protected loaderDisplay() {
		this.itemsBeforeDataLoad--;
		if (this.itemsBeforeDataLoad > 0) {
			return;
		}
		this.loader.hide();
	}

	protected displayFn(organization: FilteredOrganization | number): string {
		return organization && typeof organization === 'object' ? organization.name : '';
	}

	protected displayUsersFn(user: UserData | UserData[]): string {
		return Array.isArray(user) ? '' : `${user.firstName} ${user.lastName}`;
	}

	private _filterOrganizations(value: any): FilteredOrganization[] {
		if (value && typeof value === 'string') {
			if (value.length < 3) return this._reservationService.organizations;
			this.api.get(`/organization/?name=${value}`, (result) => {
				if (result) this.filteredOrganizations = result.results;
				else {
					this.filteredOrganizations = this._reservationService.organizations;
				}
			});
			return;
		}
		return this._reservationService.organizations;
	}

	private _filterUsers(value: any): UserData[] {
		if (value && typeof value === 'string') {
			if (value.length < 3) return this._reservationService.users;
			this.api.get(`/profile/data_full/?full_name=${value}`, (result, error) => {
				if (result) this.filteredUsers = result.results;
				else this.filteredUsers = this._reservationService.users;
			});
			return;
		}
		return this._reservationService.users;
	}

	selectOrganization($event: MatAutocompleteSelectedEvent) {
		this.data.ownerOrganization = $event.option.value;
	}

	selectUser($event: MatAutocompleteSelectedEvent) {
		this.selectedUser = $event.option.value;
	}

	submit() {
		if (this.selectedCustomerType === 1) {
			this.clientChanged.emit({
				id: this.organizationSelect.getRawValue(),
				company: true,
			});
		}
		if (this.selectedCustomerType === 0) {
			this.clientChanged.emit({
				id: this.usersSelect.getRawValue(),
				company: false,
			});
		}
		this.dialogRef.close();
	}

	changeSelectedData(value: boolean) {
		this.filteredUsers = this._reservationService.users;
		this.filteredOrganizations = this._reservationService.organizations;
		if (value) this.data.ownerOrganization = null;
		else this.selectedUser = null;
	}
}
